import { Lorebook, LorebookImportInnerData, LorebookImportRequest, LorebookVisibility } from "@next-platform/packages-ts-types";

import {
  DEFAULT_LOREBOOK_VISIBILITY,
  ENTRY_FORM_FIELDS,
  FORM_FIELDS,
  LOREBOOK_ENTRY_LIMIT_MESSAGE,
  MAX_ENTRY_KEYWORDS_COUNT,
  MAX_LOREBOOK_ENTRIES,
} from "../pages/Lorebook/constants";
import { IEntryFormData, ILorebookDataFormProps } from "../pages/Lorebook/types";

const CHARACTER_BOOK_DEFAULT_PRIORITY = 100;

const STANDARD_FIELD_VALUES = ["entryName", "keywords", "content"] as const;
type StandardFieldValue = (typeof STANDARD_FIELD_VALUES)[number];

// Import/export helpers must enforce the same entry cap as the UI/API to avoid oversized payloads
const assertLorebookEntryLimit = (count: number) => {
  if (count > MAX_LOREBOOK_ENTRIES) {
    throw new Error(LOREBOOK_ENTRY_LIMIT_MESSAGE);
  }
};

const ENTRY_NAME_SYNONYMS = ["name", "title", "entry"];
const KEYWORD_SYNONYMS = ["keyword", "keys", "keysecondary", "trigger"];
const CONTENT_SYNONYMS = ["content", "description", "text", "story", "notes", "comment", "summary", "bio"];

const FALLBACK_ENTRY_NAME_KEYS = ["name", "title"];
const FALLBACK_KEYWORD_KEYS = ["keywords", "keys", "key", "keysecondary", "trigger", "triggers", "tags"];
const FALLBACK_CONTENT_KEYS = ["content", "description", "text", "story", "notes", "comment", "summary"];

const DIRECT_MATCH_KEYS: Record<StandardFieldValue, string[]> = {
  entryName: ["name", "title"],
  keywords: ["keywords"],
  content: ["content", "text"],
};

const LOREBOOK_RESPONSE_CONTAINER_KEYS = ["data", "lorebook", "result"];

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

const extractStringFromRecord = (record: Record<string, unknown>): string | null => {
  const candidateKeys = ["value", "label", "name", "text", "tag"];

  for (const key of candidateKeys) {
    const candidate = record[key];
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }

  return null;
};

const toStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((element: unknown) => {
        if (typeof element === "string") {
          return element.trim();
        }

        if (isRecord(element)) {
          return extractStringFromRecord(element);
        }

        return null;
      })
      .filter((candidate: string | null): candidate is string => Boolean(candidate && candidate.trim()));
  }

  if (isRecord(value)) {
    const extracted = extractStringFromRecord(value);
    if (extracted) {
      return [extracted];
    }
  }

  if (typeof value === "string") {
    return value
      .split(/[,;\n]+/)
      .map((segment: string) => segment.trim())
      .filter(Boolean);
  }

  return [];
};

export const createEmptyFormValues = (): ILorebookDataFormProps => ({
  [FORM_FIELDS.NAME]: "",
  [FORM_FIELDS.DESCRIPTION]: "",
  [FORM_FIELDS.AVATAR]: "",
  [FORM_FIELDS.TAGS]: [],
  [FORM_FIELDS.ENTRIES]: [],
  [FORM_FIELDS.GUIDELINES]: false,
  [FORM_FIELDS.VISIBILITY]: LorebookVisibility.PRIVATE,
});

export const sanitizeString = (value: string) => value.replace(/\s+/g, " ").trim();

export const dedupeStrings = (values: string[]) => {
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach((value: string) => {
    const trimmed = sanitizeString(value);
    if (!trimmed) return;
    const key = trimmed.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      result.push(trimmed);
    }
  });
  return result;
};

export const isLorebookImportEnvelope = (value: unknown): value is LorebookImportRequest => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const record = value as Record<string, unknown>;
  return typeof record.spec === "string" && typeof record.data === "object" && record.data !== null;
};

interface ImportMappingField {
  key: string;
  label: string;
  recommendation?: StandardFieldValue;
  valueType?: "string" | "string[]";
}

interface ImportMappingResult {
  mapped: { source: string; target: string }[];
  skipped: string[];
}

interface GenericImportContext {
  metadata: {
    name: string;
    description: string;
    tags: string[];
    avatar: string;
  };
  entries: Record<string, unknown>[];
  directFieldMap: Record<StandardFieldValue, string[]>;
}

const normalizeKey = (key: string) => key.toLowerCase();

const determineValueType = (value: unknown): "string" | "string[]" | undefined => {
  if (typeof value === "string") return "string";
  if (Array.isArray(value) && value.some((element: unknown) => typeof element === "string")) return "string[]";
  return undefined;
};

const inferTargetFromKey = (normalizedKey: string): StandardFieldValue | undefined => {
  if (KEYWORD_SYNONYMS.some((synonym: string) => normalizedKey.includes(synonym))) {
    return "keywords";
  }
  if (CONTENT_SYNONYMS.some((synonym: string) => normalizedKey.includes(synonym))) {
    return "content";
  }
  if (ENTRY_NAME_SYNONYMS.some((synonym: string) => normalizedKey.includes(synonym))) {
    return "entryName";
  }
  return undefined;
};

const getDirectTarget = (normalizedKey: string): StandardFieldValue | undefined => {
  return STANDARD_FIELD_VALUES.find((target: StandardFieldValue) => DIRECT_MATCH_KEYS[target].includes(normalizedKey));
};

const findMatchingKey = (entry: Record<string, unknown>, key: string) => {
  if (key in entry) return key;
  const lowerKey = key.toLowerCase();
  return Object.keys(entry).find((entryKey: string) => entryKey.toLowerCase() === lowerKey) ?? key;
};

const getValueFromEntry = (entry: Record<string, unknown>, key: string) => {
  const actualKey = findMatchingKey(entry, key);
  return entry[actualKey];
};

const extractSanitizedString = (value: unknown): string => {
  if (typeof value === "string") {
    return sanitizeString(value);
  }

  if (Array.isArray(value)) {
    const stringCandidate = value.find((element: unknown): element is string => typeof element === "string" && Boolean(sanitizeString(element)));
    return stringCandidate ? sanitizeString(stringCandidate) : "";
  }

  return "";
};

const extractStringValue = (entry: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = getValueFromEntry(entry, key);
    const sanitized = extractSanitizedString(value);
    if (sanitized) {
      return sanitized;
    }
  }
  return "";
};

const extractStringArray = (entry: Record<string, unknown>, keys: string[]) => {
  const collected: string[] = [];
  keys.forEach((key: string) => {
    const value = getValueFromEntry(entry, key);
    if (!value) return;
    toStringArray(value).forEach((keyword: string) => collected.push(keyword));
  });

  return dedupeStrings(collected).slice(0, MAX_ENTRY_KEYWORDS_COUNT);
};

const resolveAvatarUrl = (record: Record<string, unknown>): string => {
  if (typeof record.avatar_url === "string") return record.avatar_url;
  if (typeof record.avatar === "string") return record.avatar;
  return "";
};

interface FieldCollectors {
  directFieldMap: Record<StandardFieldValue, string[]>;
  candidateFields: Map<string, ImportMappingField>;
}

const processEntryField = (key: string, value: unknown, collectors: FieldCollectors) => {
  const normalizedKey = normalizeKey(key);
  const valueType = determineValueType(value);
  if (!valueType) return;

  const directTarget = getDirectTarget(normalizedKey);
  if (directTarget) {
    if (!collectors.directFieldMap[directTarget].includes(key)) {
      collectors.directFieldMap[directTarget].push(key);
    }
    return;
  }

  if (!collectors.candidateFields.has(key)) {
    collectors.candidateFields.set(key, {
      key,
      label: key,
      recommendation: inferTargetFromKey(normalizedKey),
      valueType,
    });
  }
};

const prepareGenericLorebookImport = (parsed: unknown): { context: GenericImportContext; fields: ImportMappingField[] } => {
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("landing.import.error.invalidFile");
  }

  const parsedRecord = parsed as Record<string, unknown>;
  const rawEntries = parsedRecord.entries;

  if (!rawEntries || (typeof rawEntries !== "object" && !Array.isArray(rawEntries))) {
    throw new Error("landing.import.error.noEntries");
  }

  let entries: Record<string, unknown>[] = [];
  if (Array.isArray(rawEntries)) {
    entries = rawEntries.filter((entry: unknown): entry is Record<string, unknown> => !!entry && typeof entry === "object");
  } else {
    entries = Object.values(rawEntries).filter((entry: unknown): entry is Record<string, unknown> => !!entry && typeof entry === "object");
  }

  if (entries.length === 0) {
    throw new Error("landing.import.error.noEntries");
  }

  const directFieldMap: Record<StandardFieldValue, string[]> = {
    entryName: [],
    keywords: [],
    content: [],
  };

  const candidateFields = new Map<string, ImportMappingField>();

  const fieldCollectors: FieldCollectors = { directFieldMap, candidateFields };
  entries.forEach((entry: Record<string, unknown>) => {
    Object.entries(entry).forEach(([key, value]) => {
      processEntryField(key, value, fieldCollectors);
    });
  });

  const metadata = {
    name: typeof parsedRecord.name === "string" ? parsedRecord.name : "",
    description: typeof parsedRecord.description === "string" ? parsedRecord.description : "",
    tags: Array.isArray(parsedRecord.tags)
      ? (parsedRecord.tags.filter((tag: unknown): tag is string => typeof tag === "string" && tag.trim().length > 0) as string[])
      : [],
    avatar: resolveAvatarUrl(parsedRecord),
  };

  const fields = Array.from(candidateFields.values()).sort((fieldA: ImportMappingField, fieldB: ImportMappingField) => {
    const scoreA = fieldA.recommendation ? 0 : 1;
    const scoreB = fieldB.recommendation ? 0 : 1;
    if (scoreA !== scoreB) return scoreA - scoreB;
    return fieldA.label.localeCompare(fieldB.label);
  });

  return {
    context: {
      metadata,
      entries,
      directFieldMap,
    },
    fields,
  };
};

const buildDefaultMappingResult = (fields: ImportMappingField[]): ImportMappingResult => {
  const result: ImportMappingResult = { mapped: [], skipped: [] };

  fields.forEach((field: ImportMappingField) => {
    if (field.recommendation) {
      result.mapped.push({ source: field.key, target: field.recommendation });
    } else {
      result.skipped.push(field.key);
    }
  });

  return result;
};

const combineMappingSources = (
  directMap: Record<StandardFieldValue, string[]>,
  result: ImportMappingResult,
): Record<StandardFieldValue, string[]> => {
  const combined: Record<StandardFieldValue, string[]> = {
    entryName: [...directMap.entryName],
    keywords: [...directMap.keywords],
    content: [...directMap.content],
  };

  result.mapped.forEach(({ source, target }: { source: string; target: string }) => {
    const typedTarget = STANDARD_FIELD_VALUES.find((value: StandardFieldValue) => value === target);
    if (!typedTarget) return;
    if (!combined[typedTarget].includes(source)) {
      combined[typedTarget].push(source);
    }
  });

  return combined;
};

const mapEntriesToFormData = (entries: IEntryFormData[], mappingSources: Record<StandardFieldValue, string[]>): IEntryFormData[] => {
  const entryNameSources = mappingSources.entryName.length > 0 ? mappingSources.entryName : FALLBACK_ENTRY_NAME_KEYS;
  const keywordSources = mappingSources.keywords.length > 0 ? mappingSources.keywords : FALLBACK_KEYWORD_KEYS;
  const contentSources = mappingSources.content.length > 0 ? mappingSources.content : FALLBACK_CONTENT_KEYS;
  // @ts-expect-error Updating entries to accommodate business-logic IEntryFormData fields
  return entries
    .map((entry: IEntryFormData, index: number) => {
      let name = extractStringValue(entry, entryNameSources);
      const keywords = extractStringArray(entry, keywordSources);
      let content = extractStringValue(entry, contentSources);

      if (!content) {
        content = extractStringValue(entry, FALLBACK_CONTENT_KEYS);
      }

      if (!name && keywords.length > 0) {
        name = keywords[0];
      }

      const hasContent = Boolean(content);
      const hasKeywords = keywords.length > 0;
      const hasName = Boolean(name);

      if (!hasContent && !hasKeywords && !hasName) {
        return null;
      }

      const fallbackName = `Entry ${index + 1}`;

      return {
        [ENTRY_FORM_FIELDS.NAME]: name || fallbackName,
        [ENTRY_FORM_FIELDS.KEYWORDS]: keywords,
        [ENTRY_FORM_FIELDS.CONTENT]: content || "",
      };
    })
    .filter((entry: IEntryFormData | null): entry is IEntryFormData => Boolean(entry));
};

export const mapGenericLorebookToFormValues = (parsed: unknown): ILorebookDataFormProps => {
  const { context, fields } = prepareGenericLorebookImport(parsed);
  const defaultMappingResult = buildDefaultMappingResult(fields);
  const combinedSources = combineMappingSources(context.directFieldMap, defaultMappingResult);
  const entries = mapEntriesToFormData(context.entries as IEntryFormData[], combinedSources);

  if (entries.length === 0) {
    throw new Error("No entries could be mapped from the uploaded file.");
  }

  assertLorebookEntryLimit(entries.length);
  const sanitizedTags = dedupeStrings(context.metadata.tags.map(sanitizeString).filter(Boolean));

  return {
    ...createEmptyFormValues(),
    [FORM_FIELDS.NAME]: sanitizeString(context.metadata.name),
    [FORM_FIELDS.DESCRIPTION]: sanitizeString(context.metadata.description),
    [FORM_FIELDS.AVATAR]: sanitizeString(context.metadata.avatar),
    [FORM_FIELDS.TAGS]: sanitizedTags,
    [FORM_FIELDS.ENTRIES]: entries,
  };
};

const findNestedRecord = (current: Record<string, unknown>): Record<string, unknown> | undefined => {
  for (const key of LOREBOOK_RESPONSE_CONTAINER_KEYS) {
    const candidate = current[key];
    if (isRecord(candidate)) {
      return candidate;
    }
  }
  return undefined;
};

const unwrapLorebookResponse = (value: unknown): Record<string, unknown> => {
  if (!isRecord(value)) {
    return {};
  }

  let current: Record<string, unknown> = value;
  const visited = new Set<Record<string, unknown>>();

  while (!visited.has(current)) {
    visited.add(current);

    const hasEntries = Array.isArray(current.entries);
    const hasCharacterBook = "character_book" in current;

    if (hasEntries || hasCharacterBook) {
      break;
    }

    const next = findNestedRecord(current);
    if (!next) {
      break;
    }

    current = next;
  }

  return current;
};

const toRecordArray = (value: unknown): Record<string, unknown>[] => {
  if (Array.isArray(value)) {
    return value.filter((element: unknown): element is Record<string, unknown> => isRecord(element));
  }

  if (isRecord(value)) {
    return Object.values(value).filter((element: unknown): element is Record<string, unknown> => isRecord(element));
  }

  return [];
};

const collectTagStrings = (record: Record<string, unknown>): string[] => {
  const tagKeys = ["tags", "tag_list", "tagList"];
  const collected: string[] = [];

  tagKeys.forEach((key: string) => {
    if (key in record) {
      collected.push(...toStringArray(record[key]));
    }
  });

  return dedupeStrings(collected);
};

const mapEntryFromResponse = (entry: Record<string, unknown>, index: number): IEntryFormData | null => {
  const nameCandidates = ["name", "title"];
  const keywordCandidates = ["keywords", "keys", "key", "keysecondary", "secondary_keys", "trigger", "triggers", "tags"];
  const contentCandidates = ["content", "description", "text", "story", "notes", "comment", "summary", "bio"];

  let name = extractStringValue(entry, nameCandidates);
  let content = extractStringValue(entry, contentCandidates);

  const keywords = dedupeStrings(keywordCandidates.map((key: string) => toStringArray(getValueFromEntry(entry, key))).flat()).slice(
    0,
    MAX_ENTRY_KEYWORDS_COUNT,
  );

  if (!name && keywords.length > 0) {
    name = keywords[0];
  }

  if (!content) {
    content = "";
  }

  if (!name && !content && keywords.length === 0) {
    return null;
  }

  return {
    [ENTRY_FORM_FIELDS.NAME]: sanitizeString(name || `Entry ${index + 1}`),
    [ENTRY_FORM_FIELDS.KEYWORDS]: keywords,
    [ENTRY_FORM_FIELDS.CONTENT]: sanitizeString(content),
  };
};

const mapPostLorebookResponseShapeToFormValues = (lorebook: Record<string, unknown>): ILorebookDataFormProps => {
  const tags = collectTagStrings(lorebook);

  const entries = Array.isArray(lorebook.entries)
    ? (lorebook.entries
        .map((entry: unknown, index: number) => (isRecord(entry) ? mapEntryFromResponse(entry, index) : null))
        .filter((entry: IEntryFormData | null): entry is IEntryFormData => Boolean(entry)) ?? [])
    : [];

  assertLorebookEntryLimit(entries.length);
  return {
    ...createEmptyFormValues(),
    [FORM_FIELDS.NAME]: sanitizeString(typeof lorebook.name === "string" ? lorebook.name : ""),
    [FORM_FIELDS.DESCRIPTION]: sanitizeString(typeof lorebook.description === "string" ? lorebook.description : ""),
    [FORM_FIELDS.AVATAR]: sanitizeString(resolveAvatarUrl(lorebook)),
    [FORM_FIELDS.TAGS]: tags,
    [FORM_FIELDS.ENTRIES]: entries,
    [FORM_FIELDS.VISIBILITY]: typeof lorebook.visibility === "string" ? (lorebook.visibility as LorebookVisibility) : DEFAULT_LOREBOOK_VISIBILITY,
  };
};

function normalizeCharacterBook(characterBook: unknown): unknown {
  if (typeof characterBook === "string") {
    try {
      return JSON.parse(characterBook);
    } catch {
      return characterBook;
    }
  }
  return characterBook;
}

const extractCharacterBookEntries = (characterBook: unknown): unknown[] => {
  const normalized = normalizeCharacterBook(characterBook);
  if (normalized && typeof normalized === "object" && !Array.isArray(normalized)) {
    const entries = (normalized as Record<string, unknown>).entries;
    return Array.isArray(entries) ? entries : [];
  }
  return [];
};

const validateCharacterBookEntryLimit = (characterBook: unknown): void => {
  const entries = extractCharacterBookEntries(characterBook);
  assertLorebookEntryLimit(entries.length);
};

const buildGenericPayloadFromImportedLorebook = (lorebook: Record<string, unknown>): Record<string, unknown> | null => {
  const entriesFromResponse = toRecordArray(lorebook.entries);

  let entries = entriesFromResponse;

  if (entries.length === 0 && "character_book" in lorebook) {
    const normalizedCharacterBook = normalizeCharacterBook(lorebook.character_book);
    if (isRecord(normalizedCharacterBook)) {
      entries = toRecordArray(normalizedCharacterBook.entries);
    }
  }

  if (entries.length === 0) {
    return null;
  }

  return {
    name: lorebook.name,
    description: lorebook.description,
    avatar_url: lorebook.avatar_url ?? lorebook.avatar,
    tags: collectTagStrings(lorebook),
    entries,
    visibility: lorebook.visibility,
  };
};

const mergeLorebookFormValues = (primary: ILorebookDataFormProps, fallback?: ILorebookDataFormProps | null): ILorebookDataFormProps => {
  const fallbackValues = fallback ?? createEmptyFormValues();

  const name = primary[FORM_FIELDS.NAME] || fallbackValues[FORM_FIELDS.NAME];
  const description = primary[FORM_FIELDS.DESCRIPTION] || fallbackValues[FORM_FIELDS.DESCRIPTION];
  const avatar = primary[FORM_FIELDS.AVATAR] || fallbackValues[FORM_FIELDS.AVATAR];
  const tags = (primary[FORM_FIELDS.TAGS]?.length ?? 0) > 0 ? primary[FORM_FIELDS.TAGS] : fallbackValues[FORM_FIELDS.TAGS];
  const entries = (primary[FORM_FIELDS.ENTRIES]?.length ?? 0) > 0 ? primary[FORM_FIELDS.ENTRIES] : fallbackValues[FORM_FIELDS.ENTRIES];
  const visibility = primary[FORM_FIELDS.VISIBILITY] || fallbackValues[FORM_FIELDS.VISIBILITY];

  return {
    ...createEmptyFormValues(),
    [FORM_FIELDS.NAME]: name,
    [FORM_FIELDS.DESCRIPTION]: description,
    [FORM_FIELDS.AVATAR]: avatar,
    [FORM_FIELDS.TAGS]: tags,
    [FORM_FIELDS.ENTRIES]: entries,
    [FORM_FIELDS.VISIBILITY]: visibility,
  };
};

export const mapImportedLorebookToFormValues = (
  lorebook: Lorebook | Record<string, unknown>,
  externalFallback?: ILorebookDataFormProps | null,
): ILorebookDataFormProps => {
  const normalizedLorebook = unwrapLorebookResponse(lorebook);
  const directValues = mapPostLorebookResponseShapeToFormValues(normalizedLorebook);

  const genericPayload = buildGenericPayloadFromImportedLorebook(normalizedLorebook);
  const fallbackValues = genericPayload ? mapGenericLorebookToFormValues(genericPayload) : null;

  const mergedWithInternalFallback = mergeLorebookFormValues(directValues, fallbackValues);
  return mergeLorebookFormValues(mergedWithInternalFallback, externalFallback);
};

const buildCharacterBookEntriesFromFormValues = (entries: IEntryFormData[]) => {
  assertLorebookEntryLimit(entries.length);
  return entries.map((entry: IEntryFormData, index: number) => ({
    name: entry[ENTRY_FORM_FIELDS.NAME],
    keys: entry[ENTRY_FORM_FIELDS.KEYWORDS],
    secondary_keys: [],
    content: entry[ENTRY_FORM_FIELDS.CONTENT],
    enabled: true,
    insertion_order: CHARACTER_BOOK_DEFAULT_PRIORITY,
    case_sensitive: false,
    priority: CHARACTER_BOOK_DEFAULT_PRIORITY,
    id: index + 1,
    comment: "",
    selective: true,
    constant: false,
    position: "0",
    extensions: [],
    probability: CHARACTER_BOOK_DEFAULT_PRIORITY,
    selectiveLogic: 0,
  }));
};

const buildImportRequestFromFormValues = (formValues: ILorebookDataFormProps): LorebookImportRequest => {
  const name = formValues[FORM_FIELDS.NAME] || "Imported Lorebook";
  const description = formValues[FORM_FIELDS.DESCRIPTION] || "";
  const entries = formValues[FORM_FIELDS.ENTRIES] ?? [];
  assertLorebookEntryLimit(entries.length);

  const requestData: Record<string, unknown> = {
    name,
    description,
    avatar: formValues[FORM_FIELDS.AVATAR],
    avatar_url: formValues[FORM_FIELDS.AVATAR],
    tags: formValues[FORM_FIELDS.TAGS],
    visibility: formValues[FORM_FIELDS.VISIBILITY],
    personality: "",
    first_mes: "",
    mes_example: "",
    scenario: "",
    creator_notes: "",
    system_prompt: "",
    post_history_instructions: "",
    alternate_greetings: [],
    creator: "",
    character_version: "main",
    extensions: [],
    character_book: {
      name,
      description,
      extensions: {},
      entries: buildCharacterBookEntriesFromFormValues(entries),
    },
    spec: "chara_card_v2",
    spec_version: "2.0",
  };

  return {
    spec: "chara_card_v2",
    spec_version: "2.0",
    // eslint-disable-next-line id-denylist -- property name required by LorebookImportRequest interface
    data: requestData as unknown as LorebookImportInnerData,
  };
};

export const buildLorebookImportRequest = (parsed: unknown): { request: LorebookImportRequest; fallbackFormValues?: ILorebookDataFormProps } => {
  if (isLorebookImportEnvelope(parsed)) {
    const envelope = parsed as LorebookImportRequest;
    // @ts-expect-error Updating envelope.data to accommodate business-logic LorebookImportInnerData fields
    validateCharacterBookEntryLimit(envelope?.data?.character_book);
    return { request: envelope };
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    const formValues = mapGenericLorebookToFormValues(parsed);
    return { request: buildImportRequestFromFormValues(formValues), fallbackFormValues: formValues };
  }

  const record = parsed as Record<string, unknown>;

  if ("character_book" in record) {
    const requestData = {
      ...record,
      character_book: normalizeCharacterBook(record.character_book),
    } as unknown as LorebookImportInnerData;

    if (typeof requestData.spec !== "string") {
      requestData.spec = "chara_card_v2";
    }

    if (typeof requestData.spec_version !== "string") {
      requestData.spec_version = "2.0";
    }

    const request: LorebookImportRequest = {
      spec: typeof record.spec === "string" ? (record.spec as string) : "chara_card_v2",
      spec_version: typeof record.spec_version === "string" ? (record.spec_version as string) : "2.0",
      // eslint-disable-next-line id-denylist -- property name required by LorebookImportRequest interface
      data: requestData,
    };

    validateCharacterBookEntryLimit(requestData.character_book);
    return { request };
  }

  if ("entries" in record) {
    const formValues = mapGenericLorebookToFormValues(record);
    return { request: buildImportRequestFromFormValues(formValues), fallbackFormValues: formValues };
  }

  const formValues = mapGenericLorebookToFormValues(record);
  return { request: buildImportRequestFromFormValues(formValues), fallbackFormValues: formValues };
};
